import * as React from 'react'
import { AlertCircle, Eye, EyeOff, Loader2, Lock, LogIn, User } from 'lucide-react'
import { cn } from '@/lib/cn'
import { loginErrorMessage, useLogin } from '@/hooks/use-auth'
import { RegionMark } from '@/components/shell/region-plate'
import { Button } from '@/components/ui/button'

/**
 * Sign-in.
 *
 * The one screen an operator sees before they trust the rest of the product, so
 * it is worth more than a centred form. The composition:
 *
 *  - A quiet, generated backdrop rather than an image: two soft radial washes
 *    over a hairline grid. It costs no bytes, needs no network, and cannot flash
 *    an unstyled frame the way a background image can.
 *  - The card is the only lit surface on the page, which puts the eye on the
 *    single thing there is to do here.
 *  - Motion is one 400ms entrance and nothing else. A login form that keeps
 *    moving is a login form that feels slow.
 *
 * No animation library: this is the first paint of the app and the whole
 * premise is latency. Everything here is CSS the browser already has.
 */

/**
 * Cookie-bounce detection. When the server accepts the credentials but the
 * browser refuses to store the session cookie (historically: the cookie was
 * cross-site and WebKit dropped it as third-party), the sequence is 200 →
 * optimistic authenticated → first real query 401s → this page remounts. From
 * the operator's chair that is a silent flash-and-bounce with no error at all.
 * A sessionStorage stamp written on login success turns the remount into a
 * named diagnosis: back at the login page within seconds of a successful
 * login means the cookie did not stick.
 */
const LOGIN_OK_KEY = 'acs:login-succeeded-at'
const BOUNCE_WINDOW_MS = 10_000

function stampLoginSuccess(): void {
  try {
    sessionStorage.setItem(LOGIN_OK_KEY, String(Date.now()))
  } catch {
    // Storage can be unavailable (private mode, quota); losing the diagnostic
    // must never lose the login.
  }
}

function consumeRecentLoginSuccess(): boolean {
  try {
    const raw = sessionStorage.getItem(LOGIN_OK_KEY)
    if (raw === null) return false
    sessionStorage.removeItem(LOGIN_OK_KEY)
    const at = Number(raw)
    return Number.isFinite(at) && Date.now() - at < BOUNCE_WINDOW_MS
  } catch {
    return false
  }
}

export function LoginPage() {
  const login = useLogin()
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [revealed, setRevealed] = React.useState(false)
  // Lazy initializer: read (and clear) the stamp exactly once, at mount.
  const [cookieBounced] = React.useState(consumeRecentLoginSuccess)
  const userRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    userRef.current?.focus()
  }, [])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (login.isPending) return
    const u = username.trim()
    if (!u || !password) return
    login.mutate({ username: u, password }, { onSuccess: stampLoginSuccess })
  }

  const disabled = login.isPending || username.trim() === '' || password === ''

  return (
    <main className="relative grid min-h-dvh place-items-center overflow-hidden bg-s0 px-4 py-10">
      <Backdrop />

      <div className="relative w-full max-w-[400px]">
        <div
          className={cn(
            'rounded-xl border border-line bg-s1 p-7 shadow-e3',
            'motion-safe:animate-[acs-rise_.4s_cubic-bezier(.16,1,.3,1)_both]'
          )}
        >
          <Brand />

          <div className="mt-6 mb-7 space-y-1.5">
            <h1 className="font-display text-[21px] leading-tight font-semibold tracking-[-0.02em] text-fg">
              Sign in
            </h1>
            <p className="text-[13.5px] text-muted">
              Enter your operator credentials to open the console.
            </p>
          </div>

          <form onSubmit={submit} noValidate className="space-y-4">
            <Field
              ref={userRef}
              id="username"
              label="Username"
              icon={<User aria-hidden="true" />}
              value={username}
              onChange={setUsername}
              autoComplete="username"
              disabled={login.isPending}
            />

            <Field
              id="password"
              label="Password"
              icon={<Lock aria-hidden="true" />}
              value={password}
              onChange={setPassword}
              type={revealed ? 'text' : 'password'}
              autoComplete="current-password"
              disabled={login.isPending}
              trailing={
                <button
                  type="button"
                  onClick={() => setRevealed((v) => !v)}
                  // Tab order should run username -> password -> sign in. The
                  // reveal toggle is a mouse affordance and sits outside it.
                  tabIndex={-1}
                  aria-label={revealed ? 'Hide password' : 'Show password'}
                  className={cn(
                    'grid h-7 w-7 place-items-center rounded-sm text-faint',
                    'transition-colors hover:bg-s3 hover:text-fg',
                    'focus-visible:outline-2 focus-visible:outline-accent [&_svg]:size-[15px]'
                  )}
                >
                  {revealed ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              }
            />

            {/* The flash-and-bounce diagnosis. Shown only while idle: a retry
                in flight (or its own error) replaces it. */}
            {cookieBounced && login.isIdle ? (
              <p
                role="alert"
                className={cn(
                  'flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2.5',
                  'text-[13px] text-danger',
                  'motion-safe:animate-[acs-rise_.22s_ease-out_both]'
                )}
              >
                <AlertCircle aria-hidden="true" className="mt-px size-4 shrink-0" />
                <span>
                  Signed in, but your browser did not keep the session cookie. Clear this
                  site&rsquo;s data or check cookie-blocking settings, then try again.
                </span>
              </p>
            ) : null}

            {/* Reserving no space when idle keeps the card from jumping, and
                role=alert announces the failure without moving focus. */}
            {login.isError ? (
              <p
                role="alert"
                className={cn(
                  'flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2.5',
                  'text-[13px] text-danger',
                  'motion-safe:animate-[acs-rise_.22s_ease-out_both]'
                )}
              >
                <AlertCircle aria-hidden="true" className="mt-px size-4 shrink-0" />
                <span>{loginErrorMessage(login.error)}</span>
              </p>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={disabled}
              className="mt-1 w-full"
            >
              {login.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  <LogIn aria-hidden="true" />
                  Sign in
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="mt-5 text-center text-[12px] text-faint">
          Shift Console · internal use only
        </p>
      </div>

      <style>{`
        @keyframes acs-rise {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: none }
        }
      `}</style>
    </main>
  )
}

/* ------------------------------------------------------------------ *
 * Pieces
 * ------------------------------------------------------------------ */

/**
 * Says which console the operator is about to sign in to — the same flag and
 * region the rail wears, because a sign-in page for two identical consoles is
 * the first place the two get mixed up.
 */
function Brand() {
  return (
    <div className="flex items-center gap-3">
      <RegionMark size={36} />
      <span className="flex min-w-0 flex-col">
        <span className="font-display text-[15px] leading-[1.25] font-semibold tracking-[-0.01em] text-fg">
          Shift Console
        </span>
        <span className="text-[12px] leading-[1.25] font-medium text-muted">Canada</span>
      </span>
    </div>
  )
}

interface FieldProps {
  id: string
  label: string
  icon: React.ReactNode
  value: string
  onChange: (v: string) => void
  type?: string
  autoComplete?: string
  disabled?: boolean
  trailing?: React.ReactNode
}

const Field = React.forwardRef<HTMLInputElement, FieldProps>(function Field(
  { id, label, icon, value, onChange, type = 'text', autoComplete, disabled, trailing },
  ref
) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-[11px] font-medium tracking-[0.06em] text-faint uppercase"
      >
        {label}
      </label>

      <div
        className={cn(
          'group flex items-center gap-2 rounded-md border border-line bg-s1 px-3',
          'transition-[border-color,box-shadow]',
          'focus-within:border-accent focus-within:shadow-[0_0_0_3px_var(--accent-soft)]',
          disabled && 'opacity-60'
        )}
      >
        <span className="text-faint transition-colors group-focus-within:text-accent [&_svg]:size-4">
          {icon}
        </span>

        <input
          ref={ref}
          id={id}
          name={id}
          type={type}
          value={value}
          disabled={disabled}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          // 16px on phones or iOS Safari zooms the whole page on focus.
          className={cn(
            'h-11 min-w-0 flex-1 bg-transparent text-base text-fg outline-none',
            'placeholder:text-faint md:text-[14.5px]'
          )}
        />

        {trailing}
      </div>
    </div>
  )
})

/**
 * Generated backdrop — two indigo washes over a hairline grid, masked to fade
 * out before the edges. Pure CSS so it adds nothing to the network path.
 */
function Backdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.55] dark:opacity-40"
        style={{
          backgroundImage:
            'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black 20%, transparent 75%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 60% at 50% 40%, black 20%, transparent 75%)',
        }}
      />
      <div
        className="absolute -top-[18%] left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full blur-[110px]"
        style={{ background: 'var(--accent)', opacity: 0.13 }}
      />
      <div
        className="absolute -bottom-[22%] left-[18%] h-[340px] w-[520px] rounded-full blur-[120px]"
        style={{ background: 'var(--accent)', opacity: 0.07 }}
      />
    </div>
  )
}
